// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';
import Channels from '@app/features/channel/state/Channels';
import GatewayConnection from '@app/features/gateway/transport/GatewayConnection';
import type {GatewayErrorData} from '@app/features/gateway/transport/GatewaySocket';
import type {GuildReadyData} from '@app/features/gateway/types/GatewayGuildTypes';
import type {VoiceState} from '@app/features/gateway/types/GatewayVoiceTypes';
import GuildNSFWAgree from '@app/features/guild/state/GuildNSFWAgree';
import Guilds from '@app/features/guild/state/Guilds';
import GuildMembers from '@app/features/member/state/GuildMembers';
import * as NavigationCommands from '@app/features/navigation/commands/NavigationCommands';
import {SoundType} from '@app/features/notification/utils/SoundUtils';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as SoundCommands from '@app/features/ui/commands/SoundCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import Idle from '@app/features/ui/state/Idle';
import Users from '@app/features/user/state/Users';
import {getStreamKey, parseStreamKey} from '@app/features/voice/components/StreamKeys';
import {createVoiceDiagnosticLogger} from '@app/features/voice/diagnostics/VoiceDiagnostics';
import {
	hashDeviceIdForInstrumentation,
	summarizeForInstrumentation,
} from '@app/features/voice/diagnostics/VoiceDiagnosticsInstrumentation';
import {voiceStatsDB} from '@app/features/voice/diagnostics/VoiceStatsDB';
import {isPushToMuteActive} from '@app/features/voice/engine/VoiceAudioManager';
import {
	checkChannelLimit,
	checkMultipleConnections,
	sendVoiceStateConnect,
	sendVoiceStateDisconnect,
} from '@app/features/voice/engine/VoiceChannelConnector';
import type {VoiceServerUpdateData} from '@app/features/voice/engine/VoiceConnectionManager';
import VoiceConnectionManager from '@app/features/voice/engine/VoiceConnectionManager';
import VoiceDevicePermissionState from '@app/features/voice/engine/VoiceDevicePermissionState';
import VoiceMediaManager from '@app/features/voice/engine/VoiceMediaManager';
import VoiceMediaStateCoordinator from '@app/features/voice/engine/VoiceMediaStateCoordinator';
import {bindOutputDeviceSync} from '@app/features/voice/engine/VoiceOutputDeviceSync';
import type {LivekitParticipantSnapshot} from '@app/features/voice/engine/VoiceParticipantManager';
import VoiceParticipantManager from '@app/features/voice/engine/VoiceParticipantManager';
import VoicePermissionManager from '@app/features/voice/engine/VoicePermissionManager';
import {bindRoomEvents} from '@app/features/voice/engine/VoiceRoomEventBinder';
import type {DeviceScreenShareCaptureOptions} from '@app/features/voice/engine/VoiceScreenShareManager';
import VoiceStateManager from '@app/features/voice/engine/VoiceStateManager';
import {VoiceStateSyncManager, type VoiceStateSyncPayload} from '@app/features/voice/engine/VoiceStateSyncManager';
import type {
	LatencyDataPoint,
	PerTrackStats,
	TransportInfo,
	VoiceStats,
	VoiceStatsSample,
} from '@app/features/voice/engine/VoiceStatsManager';
import {VoiceStatsManager} from '@app/features/voice/engine/VoiceStatsManager';
import {normalizeStreamGuildId, stopWatchingStreamKey} from '@app/features/voice/engine/VoiceStreamWatchState';
import VoiceSubscriptionManager from '@app/features/voice/engine/VoiceSubscriptionManager';
import CallMediaPrefs from '@app/features/voice/state/CallMediaPrefs';
import LocalVoiceState from '@app/features/voice/state/LocalVoiceState';
import MediaPermission from '@app/features/voice/state/MediaPermission';
import ParticipantVolume from '@app/features/voice/state/ParticipantVolume';
import StreamAudioPrefs from '@app/features/voice/state/StreamAudioPrefs';
import VoiceCallLayout from '@app/features/voice/state/VoiceCallLayout';
import VoiceSessionRestore, {type VoiceSessionRestoreSnapshot} from '@app/features/voice/state/VoiceSessionRestore';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import {ME} from '@app/vendor/constants/AppConstants';
import {ChannelTypes} from '@app/vendor/constants/ChannelConstants';
import type {GatewayErrorCode} from '@app/vendor/constants/GatewayConstants';
import {GatewayErrorCodes} from '@app/vendor/constants/GatewayConstants';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import type {Participant, Room, ScreenShareCaptureOptions, TrackPublishOptions} from 'livekit-client';
import {computed, makeObservable, reaction} from 'mobx';

const logger = new Logger('MediaEngineFacade');
const diagnostics = createVoiceDiagnosticLogger('voice.MediaEngineFacade');
const AFK_CHECK_INTERVAL_MS = 10000;
const DEFERRED_DISCONNECT_TIMEOUT_MS = 5000;
const WEBRTC_STATS_INTERVAL_MS = 10000;
type DiagState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'disconnected';
type LivekitRoomLike = {
	on: (event: string, listener: (...args: Array<unknown>) => void) => unknown;
	off?: (event: string, listener: (...args: Array<unknown>) => void) => unknown;
	state?: string;
	engine?: {
		pcManager?: {
			publisher?: {
				getStats?: () => Promise<RTCStatsReport>;
			};
			subscriber?: {
				getStats?: () => Promise<RTCStatsReport>;
			};
		};
	};
	localParticipant?: {
		identity?: string;
		sid?: string;
		on?: (event: string, listener: (...args: Array<unknown>) => void) => unknown;
		off?: (event: string, listener: (...args: Array<unknown>) => void) => unknown;
	};
	remoteParticipants?:
		| Map<unknown, unknown>
		| {
				size?: number;
		  };
};
const LIVEKIT_ROOM_EVENTS: ReadonlyArray<string> = [
	'connected',
	'reconnecting',
	'reconnected',
	'disconnected',
	'connectionStateChanged',
	'mediaDevicesError',
	'participantConnected',
	'participantDisconnected',
	'trackPublished',
	'trackUnpublished',
	'trackSubscribed',
	'trackUnsubscribed',
	'trackSubscriptionFailed',
	'trackMuted',
	'trackUnmuted',
	'activeSpeakersChanged',
	'audioPlaybackChanged',
	'mediaDevicesChanged',
	'connectionQualityChanged',
	'dataReceived',
	'roomMetadataChanged',
	'encryptionError',
	'signalConnected',
	'participantPermissionsChanged',
	'localTrackPublished',
	'localTrackUnpublished',
];
function summarizeParticipantArg(value: unknown): unknown {
	if (!value || typeof value !== 'object') return summarizeForInstrumentation(value);
	const p = value as {
		identity?: string;
		sid?: string;
		isLocal?: boolean;
	};
	return {identity: p.identity ?? null, sid: p.sid ?? null, isLocal: p.isLocal ?? null};
}
function summarizeTrackPublicationArg(value: unknown): unknown {
	if (!value || typeof value !== 'object') return summarizeForInstrumentation(value);
	const pub = value as {
		trackSid?: string;
		trackName?: string;
		kind?: string;
		source?: string;
		isMuted?: boolean;
		isSubscribed?: boolean;
		isEnabled?: boolean;
		mimeType?: string;
	};
	return {
		trackSid: pub.trackSid ?? null,
		trackName: pub.trackName ?? null,
		kind: pub.kind ?? null,
		source: pub.source ?? null,
		isMuted: pub.isMuted ?? null,
		isSubscribed: pub.isSubscribed ?? null,
		isEnabled: pub.isEnabled ?? null,
		mimeType: pub.mimeType ?? null,
	};
}
function summarizeLivekitArg(value: unknown): unknown {
	if (value == null) return value;
	if (typeof value !== 'object') return summarizeForInstrumentation(value);
	const obj = value as Record<string, unknown>;
	if ('identity' in obj && 'sid' in obj && typeof obj.identity === 'string') {
		return summarizeParticipantArg(obj);
	}
	if ('trackSid' in obj || 'source' in obj) {
		return summarizeTrackPublicationArg(obj);
	}
	if (Array.isArray(value)) {
		return value.slice(0, 32).map(summarizeLivekitArg);
	}
	return summarizeForInstrumentation(obj);
}
function statsReportToArray(report: RTCStatsReport | undefined): Array<Record<string, unknown>> {
	if (!report) return [];
	const out: Array<Record<string, unknown>> = [];
	report.forEach((entry: Record<string, unknown>) => {
		const type = entry.type as string | undefined;
		if (
			type === 'outbound-rtp' ||
			type === 'inbound-rtp' ||
			type === 'remote-inbound-rtp' ||
			type === 'remote-outbound-rtp' ||
			type === 'candidate-pair' ||
			type === 'transport'
		) {
			const filtered: Record<string, unknown> = {};
			const keys = [
				'id',
				'type',
				'kind',
				'mid',
				'ssrc',
				'bytesSent',
				'bytesReceived',
				'packetsSent',
				'packetsReceived',
				'packetsLost',
				'jitter',
				'framesPerSecond',
				'frameWidth',
				'frameHeight',
				'roundTripTime',
				'totalRoundTripTime',
				'currentRoundTripTime',
				'availableOutgoingBitrate',
				'availableIncomingBitrate',
				'state',
				'nominated',
				'selected',
				'localCandidateId',
				'remoteCandidateId',
				'selectedCandidatePairId',
				'audioLevel',
				'totalAudioEnergy',
				'concealmentEvents',
				'silentConcealedSamples',
				'qualityLimitationReason',
				'qualityLimitationDurations',
				'mediaSourceId',
				'targetBitrate',
			];
			for (const key of keys) {
				if (key in entry) {
					filtered[key] = entry[key];
				}
			}
			out.push(filtered);
		}
	});
	return out;
}
class MediaEngineFacade {
	private statsManager: VoiceStatsManager;
	private afkIntervalId: ReturnType<typeof setInterval> | null = null;
	private outputDeviceSyncDisposer: (() => void) | null = null;
	private audioPreferencesSyncDisposer: (() => void) | null = null;
	private voiceStateSync = new VoiceStateSyncManager();
	private i18n: I18n | null = null;
	private pendingServerDisconnectTimeout: ReturnType<typeof setTimeout> | null = null;
	private pendingServerDisconnectConnectionId: string | null = null;
	private activeConnection: {
		guildId: string | null;
		channelId: string | null;
	} | null = null;
	private pendingUserMove: {
		guildId: string | null;
		channelId: string;
	} | null = null;
	private pendingSessionRestore: {
		guildId: string | null;
		channelId: string;
		restoreVideo: boolean;
		restoreStream: boolean;
	} | null = null;
	private diagState: DiagState = 'idle';
	private diagRoomDisposers: Array<() => void> = [];
	private diagStatsTimer: ReturnType<typeof setInterval> | null = null;
	private diagAttemptId: number | null = null;
	constructor() {
		this.statsManager = new VoiceStatsManager();
		makeObservable(this, {
			room: computed,
			guildId: computed,
			channelId: computed,
			connectionId: computed,
			connected: computed,
			connecting: computed,
			voiceServerEndpoint: computed,
			participants: computed,
			connectionVoiceStates: computed,
			currentLatency: computed,
			averageLatency: computed,
			latencyHistory: computed,
			voiceStats: computed,
			perTrackStats: computed,
			statsTimeSeries: computed,
			publisherTransport: computed,
			subscriberTransport: computed,
			displayLatency: computed,
			estimatedLatency: computed,
		});
		this.initializeLocalAudioStateSync();
		this.initializeVoiceSessionRestoreSync();
		(
			window as typeof window & {
				_mediaEngine?: MediaEngineFacade;
			}
		)._mediaEngine = this;
		logger.debug('MediaEngineFacade initialized');
	}
	setI18n(i18n: I18n): void {
		this.i18n = i18n;
	}
	get room(): Room | null {
		return VoiceConnectionManager.room;
	}
	get guildId(): string | null {
		return VoiceConnectionManager.guildId;
	}
	get channelId(): string | null {
		return VoiceConnectionManager.channelId;
	}
	get connectionId(): string | null {
		return VoiceConnectionManager.connectionId;
	}
	get connected(): boolean {
		return VoiceConnectionManager.connected;
	}
	get connecting(): boolean {
		return VoiceConnectionManager.connecting;
	}
	get voiceServerEndpoint(): string | null {
		return VoiceConnectionManager.voiceServerEndpoint;
	}
	get participants(): Readonly<Record<string, LivekitParticipantSnapshot>> {
		return VoiceParticipantManager.participants;
	}
	get connectionVoiceStates(): Readonly<Record<string, VoiceState>> {
		return VoiceStateManager.getConnectionVoiceStates();
	}
	get currentLatency(): number | null {
		return this.statsManager.currentLatency;
	}
	get averageLatency(): number | null {
		return this.statsManager.averageLatency;
	}
	get latencyHistory(): Array<LatencyDataPoint> {
		return this.statsManager.latencyHistory;
	}
	get voiceStats(): VoiceStats {
		return this.statsManager.voiceStats;
	}
	get perTrackStats(): Array<PerTrackStats> {
		return this.statsManager.perTrackStats;
	}
	get statsTimeSeries(): Array<VoiceStatsSample> {
		return this.statsManager.statsTimeSeries;
	}
	get publisherTransport(): TransportInfo | null {
		return this.statsManager.publisherTransport;
	}
	get subscriberTransport(): TransportInfo | null {
		return this.statsManager.subscriberTransport;
	}
	get estimatedLatency(): number | null {
		return this.statsManager.estimatedLatency;
	}
	get displayLatency(): number | null {
		return this.statsManager.displayLatency;
	}
	private initializeLocalAudioStateSync(): void {
		reaction(
			() => ({
				selfMute: LocalVoiceState.getSelfMute(),
				selfDeaf: LocalVoiceState.getSelfDeaf(),
			}),
			(currentState, previousState) => {
				logger.debug('Local audio state changed, reconciling active voice session', {
					previousState,
					currentState,
				});
				void this.reconcileLocalAudioState('local audio state change');
			},
			{
				name: 'MediaEngineFacade-localAudioStateSync',
			},
		);
	}
	private initializeVoiceSessionRestoreSync(): void {
		reaction(
			() => ({
				currentUserId: Authentication?.currentUserId ?? null,
				connected: VoiceConnectionManager.connected,
				guildId: VoiceConnectionManager.guildId,
				channelId: VoiceConnectionManager.channelId,
				selfVideo: LocalVoiceState.getSelfVideo(),
				selfStream: LocalVoiceState.getSelfStream(),
			}),
			({currentUserId, connected, guildId, channelId, selfVideo, selfStream}) => {
				if (!connected || !currentUserId || !channelId) {
					return;
				}
				VoiceSessionRestore.saveSnapshot({
					userId: currentUserId,
					guildId,
					channelId,
					selfVideo,
					selfStream,
				});
			},
			{
				name: 'MediaEngineFacade-voiceSessionRestoreSync',
			},
		);
	}
	async connectToVoiceChannel(guildId: string | null, channelId: string): Promise<void> {
		VoiceCallLayout.reset();
		const currentUserId = Authentication.currentUserId;
		const isTimedOut =
			guildId && currentUserId ? (GuildMembers.getMember(guildId, currentUserId)?.isTimedOut() ?? false) : false;
		if (isTimedOut) {
			if (!this.i18n) {
				throw new Error('MediaEngineFacade: i18n not initialized');
			}
			ToastCommands.createToast({
				type: 'error',
				children: this.i18n._(msg`You can't join while you're on timeout.`),
			});
			return;
		}
		const currentUser = Users.getCurrentUser();
		const isUnclaimed = !(currentUser?.isClaimed() ?? false);
		if (isUnclaimed) {
			if (!this.i18n) {
				throw new Error('MediaEngineFacade: i18n not initialized');
			}
			if (guildId) {
				const guild = Guilds.getGuild(guildId);
				const isOwner = guild?.isOwner(currentUserId) ?? false;
				if (!isOwner) {
					ToastCommands.createToast({
						type: 'error',
						children: this.i18n._(msg`Claim your account to join voice channels you don't own.`),
					});
					return;
				}
			} else {
				const channel = Channels.getChannel(channelId);
				if (channel?.type === ChannelTypes.DM) {
					ToastCommands.createToast({
						type: 'error',
						children: this.i18n._(msg`Claim your account to start or join 1:1 calls.`),
					});
					return;
				}
			}
		}
		if (!GatewayConnection.socket) {
			logger.warn('No socket');
			return;
		}
		const voiceChannel = Channels.getChannel(channelId);
		if (voiceChannel && GuildNSFWAgree.shouldShowGate({channelId, guildId})) {
			NavigationCommands.selectChannel(guildId ?? undefined, channelId);
			return;
		}
		if (!checkChannelLimit(guildId, channelId)) return;
		this.voiceStateSync.reset();
		const shouldProceed = checkMultipleConnections(
			guildId,
			channelId,
			async () => this.connectDirectly(guildId, channelId),
			() => this.connectDirectly(guildId, channelId),
			() => VoiceConnectionManager.clearInFlightConnect(),
		);
		if (!shouldProceed) return;
		await this.connectDirectly(guildId, channelId, 'connectToVoiceChannel');
	}
	private async connectDirectly(
		guildId: string | null,
		channelId: string,
		reason: 'connectToVoiceChannel' | 'connectDirectly' = 'connectDirectly',
	): Promise<void> {
		VoiceCallLayout.reset();
		if (VoiceConnectionManager.connected || VoiceConnectionManager.connecting) {
			if (VoiceConnectionManager.channelId === channelId && VoiceConnectionManager.guildId === guildId) {
				return;
			}
		}
		const canStartVoiceConnect = await VoiceMediaManager.prepareMicrophonePermissionForConnect();
		if (!canStartVoiceConnect) return;
		if (VoiceConnectionManager.connected || VoiceConnectionManager.connecting) {
			this.pendingUserMove = {guildId, channelId};
			this.disconnectForChannelMove('user');
		}
		this.voiceStateSync.reset();
		if (!VoiceConnectionManager.startConnection(guildId, channelId)) return;
		this.setDiagState('connecting', reason, {guildId, channelId});
		void this.logSessionStart(guildId, channelId);
		this.navigateToVoiceChannel(guildId, channelId);
		sendVoiceStateConnect(guildId, channelId);
	}
	async restoreVoiceSession(
		snapshot: VoiceSessionRestoreSnapshot,
		options?: {
			restoreVideo?: boolean;
			restoreStream?: boolean;
		},
	): Promise<void> {
		this.pendingSessionRestore = {
			guildId: snapshot.guildId,
			channelId: snapshot.channelId,
			restoreVideo: Boolean(options?.restoreVideo && snapshot.selfVideo),
			restoreStream: Boolean(options?.restoreStream && snapshot.selfStream),
		};
		await this.connectToVoiceChannel(snapshot.guildId, snapshot.channelId);
		if (
			!VoiceConnectionManager.connecting &&
			!(
				VoiceConnectionManager.connected &&
				VoiceConnectionManager.guildId === snapshot.guildId &&
				VoiceConnectionManager.channelId === snapshot.channelId
			)
		) {
			this.pendingSessionRestore = null;
		}
	}
	async disconnectFromVoiceChannel(reason: 'user' | 'error' | 'server' = 'user'): Promise<void> {
		const {guildId, connectionId, connected, connecting, channelId} = VoiceConnectionManager.connectionState;
		if (!connected && !connecting && !channelId) return;
		logger.debug('Voice teardown starting', {guildId, channelId, reason});
		this.cancelPendingServerDisconnect();
		this.clearViewerStreamKeys();
		this.stopTracking();
		this.statsManager.reset();
		this.activeConnection = null;
		this.pendingUserMove = null;
		this.pendingSessionRestore = null;
		if (reason === 'user' || reason === 'server') {
			VoiceSessionRestore.clearSnapshot();
		}
		if (reason !== 'server' && connectionId) {
			sendVoiceStateDisconnect(guildId, connectionId);
		}
		if (reason === 'user') {
			SoundCommands.playSound(SoundType.VoiceDisconnect);
		}
		VoiceCallLayout.reset();
		VoiceMediaStateCoordinator.resetLocalMediaState('disconnect');
		VoiceMediaManager.resetStreamTracking();
		VoiceParticipantManager.clear();
		this.voiceStateSync.reset();
		LocalVoiceState.clearConnectionState(connectionId);
		VoiceConnectionManager.disconnectFromVoiceChannel(reason);
		if (reason === 'user' || reason === 'server') {
			VoiceSessionRestore.clearSnapshot();
		}
		if (connectionId) CallMediaPrefs.clearForCall(connectionId);
		this.detachDiagnosticsFromRoom();
		this.setDiagState('idle', `disconnect:${reason}`);
		logger.info('Voice teardown complete', {channelId, reason});
	}
	private disconnectForChannelMove(reason: 'user' | 'server'): void {
		const {guildId, connectionId, connected, connecting, channelId} = VoiceConnectionManager.connectionState;
		if (!connected && !connecting && !channelId) return;
		logger.debug('Voice teardown for channel move', {guildId, channelId});
		this.cancelPendingServerDisconnect();
		this.clearViewerStreamKeys();
		this.stopTracking();
		this.statsManager.reset();
		this.activeConnection = null;
		this.pendingSessionRestore = null;
		if (reason === 'server') {
			this.pendingUserMove = null;
		}
		if (reason === 'user' && connectionId) {
			sendVoiceStateDisconnect(guildId, connectionId);
		}
		VoiceCallLayout.reset();
		VoiceMediaStateCoordinator.resetLocalMediaState('disconnect');
		VoiceMediaManager.resetStreamTracking();
		VoiceParticipantManager.clear();
		this.voiceStateSync.reset();
		LocalVoiceState.clearConnectionState(connectionId);
		VoiceConnectionManager.disconnectForChannelMove();
		if (connectionId) CallMediaPrefs.clearForCall(connectionId);
		SoundCommands.playSound(SoundType.UserMove);
	}
	private scheduleDeferredServerDisconnect(connectionId: string): void {
		this.cancelPendingServerDisconnect();
		this.pendingServerDisconnectConnectionId = connectionId;
		this.pendingServerDisconnectTimeout = setTimeout(() => {
			if (
				this.pendingServerDisconnectConnectionId === connectionId &&
				VoiceConnectionManager.connectionId === connectionId &&
				VoiceConnectionManager.connected
			) {
				logger.info('Deferred disconnect executing - no VOICE_SERVER_UPDATE received', {connectionId});
				void this.disconnectFromVoiceChannel('server');
			}
			this.pendingServerDisconnectTimeout = null;
			this.pendingServerDisconnectConnectionId = null;
		}, DEFERRED_DISCONNECT_TIMEOUT_MS);
		logger.debug('Scheduled deferred disconnect', {connectionId, timeoutMs: DEFERRED_DISCONNECT_TIMEOUT_MS});
	}
	private cancelPendingServerDisconnect(): void {
		if (this.pendingServerDisconnectTimeout) {
			clearTimeout(this.pendingServerDisconnectTimeout);
			this.pendingServerDisconnectTimeout = null;
			logger.debug('Cancelled pending server disconnect', {
				connectionId: this.pendingServerDisconnectConnectionId,
			});
		}
		this.pendingServerDisconnectConnectionId = null;
	}
	handleVoiceServerUpdate(raw: VoiceServerUpdateData): void {
		if (raw.connection_id && raw.connection_id === this.pendingServerDisconnectConnectionId) {
			this.cancelPendingServerDisconnect();
		}
		const expectedChannelId = VoiceConnectionManager.channelId;
		const currentVoiceState = raw.connection_id
			? VoiceStateManager.getVoiceStateByConnectionId(raw.connection_id)
			: null;
		if (
			VoiceConnectionManager.connected &&
			raw.channel_id &&
			currentVoiceState?.channel_id &&
			currentVoiceState.channel_id !== raw.channel_id
		) {
			logger.warn('Ignoring VOICE_SERVER_UPDATE that conflicts with known voice state', {
				expectedChannelId,
				incomingChannelId: raw.channel_id,
				connectionId: raw.connection_id,
				voiceStateChannelId: currentVoiceState.channel_id,
			});
			return;
		}
		if (
			raw.channel_id &&
			expectedChannelId &&
			raw.channel_id !== expectedChannelId &&
			this.pendingUserMove?.channelId === expectedChannelId &&
			VoiceConnectionManager.connecting
		) {
			logger.warn('Ignoring VOICE_SERVER_UPDATE during user-initiated move', {
				expectedChannelId,
				incomingChannelId: raw.channel_id,
				connectionId: raw.connection_id,
			});
			return;
		}
		const resolvedGuildId = raw.guild_id ?? VoiceConnectionManager.guildId ?? null;
		const resolvedChannelId = raw.channel_id ?? VoiceConnectionManager.channelId ?? null;
		if (raw.connection_id) {
			LocalVoiceState.seedConnectionState(raw.connection_id, {
				selfMute: currentVoiceState?.self_mute ?? LocalVoiceState.getSelfMute(),
				selfDeaf: currentVoiceState?.self_deaf ?? LocalVoiceState.getSelfDeaf(),
				selfVideo: currentVoiceState?.self_video ?? LocalVoiceState.getSelfVideo(),
				selfStream: currentVoiceState?.self_stream ?? LocalVoiceState.getSelfStream(),
				viewerStreamKeys: currentVoiceState?.viewer_stream_keys ?? LocalVoiceState.getViewerStreamKeys(),
			});
		}
		const shouldPreserveLocalMedia =
			Boolean(this.activeConnection) &&
			this.activeConnection?.guildId === resolvedGuildId &&
			this.activeConnection?.channelId === resolvedChannelId;
		const shouldApplyPendingSessionRestore =
			Boolean(this.pendingSessionRestore) &&
			this.pendingSessionRestore?.guildId === resolvedGuildId &&
			this.pendingSessionRestore?.channelId === resolvedChannelId;
		VoiceConnectionManager.handleVoiceServerUpdate(
			raw,
			(room, attemptId, guildId, channelId) => {
				this.attachDiagnosticsToRoom(room, attemptId);
				bindRoomEvents(room, attemptId, guildId, channelId, {
					onConnected: async () => {
						this.activeConnection = {guildId, channelId};
						this.pendingUserMove = null;
						this.setDiagState('connected', 'room.onConnected', {guildId, channelId, attemptId});
						this.navigateToVoiceChannel(guildId, channelId);
						this.startTracking(room);
						if (shouldPreserveLocalMedia) {
							await this.restoreLocalMediaState();
						}
						if (shouldApplyPendingSessionRestore) {
							await this.restorePendingSessionMedia();
						}
						await this.reconcileLocalAudioState('voice room connected');
					},
					onDisconnected: () => {
						this.setDiagState('disconnected', 'room.onDisconnected', {attemptId});
						this.stopTracking();
						this.statsManager.reset();
						this.detachDiagnosticsFromRoom();
					},
					onReconnecting: () => {
						this.setDiagState('reconnecting', 'room.onReconnecting', {attemptId});
						this.statsManager.stopLatencyTracking();
						this.statsManager.stopStatsTracking();
					},
					onReconnected: () => {
						this.setDiagState('connected', 'room.onReconnected', {attemptId});
						this.statsManager.startLatencyTracking();
						this.statsManager.startStatsTracking();
						this.voiceStateSync.reset();
						void this.reconcileLocalAudioState('voice room reconnected');
					},
				});
			},
			(isChannelMove) => {
				this.cancelPendingServerDisconnect();
				this.stopTracking();
				if (isChannelMove) {
					this.statsManager.reset();
				}
				VoiceParticipantManager.clear();
				this.voiceStateSync.reset();
				if (!shouldPreserveLocalMedia && this.activeConnection) {
					VoiceMediaStateCoordinator.resetLocalMediaState('disconnect');
					VoiceMediaManager.resetStreamTracking();
				}
			},
		);
	}
	handleConnectionOpen(guilds: Array<GuildReadyData>): void {
		VoiceStateManager.handleConnectionOpen(guilds);
	}
	handleGuildCreate(guild: GuildReadyData): void {
		VoiceStateManager.handleGuildCreate(guild);
	}
	handleGuildDelete(guildId: string): void {
		VoiceStateManager.handleGuildDelete(guildId);
		if (VoiceConnectionManager.connected && VoiceConnectionManager.guildId === guildId) {
			void this.disconnectFromVoiceChannel('server');
		}
	}
	handlePassiveVoiceStates(guildId: string, voiceStates: Array<VoiceState>): void {
		for (const voiceState of voiceStates) {
			const stateWithGuild = {
				...voiceState,
				guild_id: voiceState.guild_id ?? guildId,
			};
			VoiceStateManager.handleGatewayVoiceStateUpdate(guildId, stateWithGuild);
		}
	}
	handleGatewayVoiceStateUpdate(guildId: string | null, voiceState: VoiceState): void {
		const user = Users.getCurrentUser();
		const isLocalConnection =
			user && voiceState.user_id === user.id && voiceState.connection_id === VoiceConnectionManager.connectionId;
		const previousConnectionState = voiceState.connection_id
			? VoiceStateManager.getVoiceStateByConnectionId(voiceState.connection_id)
			: null;
		const previousLocalMediaState = isLocalConnection
			? {
					selfVideo: LocalVoiceState.getSelfVideo(),
					selfStream: LocalVoiceState.getSelfStream(),
				}
			: null;
		const previousLocalAudioState =
			isLocalConnection && previousConnectionState
				? {
						serverMute: previousConnectionState.mute,
						serverDeaf: previousConnectionState.deaf,
						selfMute: previousConnectionState.self_mute,
						selfDeaf: previousConnectionState.self_deaf,
					}
				: null;
		const previousViewerStreamKeys = previousConnectionState?.viewer_stream_keys ?? [];
		VoiceStateManager.handleGatewayVoiceStateUpdate(guildId, voiceState);
		if (!isLocalConnection && VoiceConnectionManager.connected && voiceState.channel_id) {
			const newViewerStreamKeys = voiceState.viewer_stream_keys ?? [];
			if (JSON.stringify(previousViewerStreamKeys) !== JSON.stringify(newViewerStreamKeys)) {
				this.playSpectatorSounds(previousViewerStreamKeys, newViewerStreamKeys);
			}
		}
		if (!isLocalConnection && voiceState.connection_id) {
			const previousGuildId = normalizeStreamGuildId(previousConnectionState?.guild_id);
			const nextGuildId = normalizeStreamGuildId(guildId ?? voiceState.guild_id);
			const previousChannelId = previousConnectionState?.channel_id ?? null;
			const nextChannelId = voiceState.channel_id ?? null;
			const leftPreviousStreamChannel =
				previousChannelId != null && (previousChannelId !== nextChannelId || previousGuildId !== nextGuildId);
			const streamEnded = voiceState.self_stream === false;
			const streamChannelId = leftPreviousStreamChannel ? previousChannelId : nextChannelId;
			const streamGuildId = leftPreviousStreamChannel ? previousGuildId : nextGuildId;
			if (streamChannelId && (streamEnded || leftPreviousStreamChannel)) {
				const streamKey = getStreamKey(streamGuildId, streamChannelId, voiceState.connection_id);
				stopWatchingStreamKey(streamKey, {
					guildId: streamGuildId,
					channelId: streamChannelId,
				});
			}
		}
		if (isLocalConnection) {
			const currentConnectionId = VoiceConnectionManager.connectionId;
			const isCurrentConnection = voiceState.connection_id === currentConnectionId;
			if (voiceState.channel_id && voiceState.connection_id && isCurrentConnection) {
				const ptmActive = isPushToMuteActive();
				LocalVoiceState.syncConnectionState(voiceState.connection_id, {
					selfMute: ptmActive ? undefined : voiceState.self_mute,
					selfDeaf: voiceState.self_deaf,
					selfVideo: voiceState.self_video,
					selfStream: voiceState.self_stream,
					viewerStreamKeys: voiceState.viewer_stream_keys ?? [],
				});
			}
			const serverPayload =
				voiceState.channel_id && voiceState.connection_id
					? {
							guild_id: guildId,
							channel_id: voiceState.channel_id,
							connection_id: voiceState.connection_id,
							self_mute: voiceState.self_mute,
							self_deaf: voiceState.self_deaf,
							self_video: voiceState.self_video,
							self_stream: voiceState.self_stream,
							viewer_stream_keys: voiceState.viewer_stream_keys ?? [],
						}
					: null;
			this.voiceStateSync.confirmServerState(serverPayload);
			if (voiceState.channel_id && voiceState.connection_id) {
				if (isCurrentConnection && this.pendingServerDisconnectConnectionId === voiceState.connection_id) {
					this.cancelPendingServerDisconnect();
				}
				if (
					isCurrentConnection &&
					VoiceConnectionManager.connected &&
					!VoiceConnectionManager.connecting &&
					voiceState.channel_id !== VoiceConnectionManager.channelId
				) {
					const updatedGuildId = guildId ?? voiceState.guild_id ?? null;
					this.disconnectForChannelMove('server');
					VoiceConnectionManager.recoverConnectionExpectation(updatedGuildId, voiceState.channel_id);
				}
				const shouldRecoverConnectionExpectation =
					!VoiceConnectionManager.connected &&
					!VoiceConnectionManager.connecting &&
					VoiceConnectionManager.guildId === null &&
					VoiceConnectionManager.channelId === null;
				if (shouldRecoverConnectionExpectation) {
					const recoveredGuildId = guildId ?? voiceState.guild_id ?? null;
					VoiceConnectionManager.recoverConnectionExpectation(recoveredGuildId, voiceState.channel_id);
					logger.info('Recovered connection expectation from voice state update', {
						guildId: recoveredGuildId,
						channelId: voiceState.channel_id,
						connectionId: voiceState.connection_id,
					});
				}
				const audioStateChanged =
					previousLocalAudioState &&
					(previousLocalAudioState.serverMute !== voiceState.mute ||
						previousLocalAudioState.serverDeaf !== voiceState.deaf ||
						previousLocalAudioState.selfMute !== voiceState.self_mute ||
						previousLocalAudioState.selfDeaf !== voiceState.self_deaf);
				if (isCurrentConnection && audioStateChanged) {
					void VoiceMediaManager.reconcileEffectiveAudioState(VoiceConnectionManager.room, {
						channelId: voiceState.channel_id,
						serverMute: voiceState.mute,
						serverDeaf: voiceState.deaf,
					});
				}
			}
			if (previousLocalMediaState) {
				const videoDisabled = previousLocalMediaState.selfVideo && voiceState.self_video === false;
				const streamDisabled = previousLocalMediaState.selfStream && voiceState.self_stream === false;
				if (videoDisabled) {
					logger.info('Server disabled camera for local connection, unpublishing video track');
					void this.setCameraEnabled(false, {sendUpdate: false});
				}
				if (streamDisabled) {
					logger.info('Server disabled screen share for local connection, unpublishing stream track');
					void this.setScreenShareEnabled(false, {sendUpdate: false});
				}
			}
			if (
				voiceState.channel_id === null &&
				VoiceConnectionManager.connected &&
				voiceState.connection_id &&
				isCurrentConnection
			) {
				this.scheduleDeferredServerDisconnect(voiceState.connection_id);
			}
		}
	}
	handleGatewayVoiceStateDelete(guildId: string, userId: string): void {
		VoiceStateManager.handleGatewayVoiceStateDelete(guildId, userId);
	}
	getCurrentUserVoiceState(guildId?: string | null): VoiceState | null {
		return VoiceStateManager.getCurrentUserVoiceState(
			guildId,
			Users.getCurrentUser()?.id,
			VoiceConnectionManager.connectionId,
		);
	}
	getVoiceState(guildId: string | null, userId?: string): VoiceState | null {
		const currentUserId = Users.getCurrentUser()?.id;
		const connectionId = !userId || userId === currentUserId ? VoiceConnectionManager.connectionId : null;
		return VoiceStateManager.getVoiceState(guildId, userId, currentUserId, connectionId);
	}
	getVoiceStateByConnectionId(connectionId: string): VoiceState | null {
		return VoiceStateManager.getVoiceStateByConnectionId(connectionId);
	}
	getAllVoiceStatesInChannel(guildId: string, channelId: string): Readonly<Record<string, VoiceState>> {
		return VoiceStateManager.getAllVoiceStatesInChannel(guildId, channelId);
	}
	getAllVoiceStatesInGuild(
		guildId: string,
	): Readonly<Record<string, Readonly<Record<string, VoiceState>>>> | undefined {
		return VoiceStateManager.getAllVoiceStatesInGuild(guildId);
	}
	getAllVoiceStates(): Readonly<Record<string, Readonly<Record<string, Readonly<Record<string, VoiceState>>>>>> {
		return VoiceStateManager.getAllVoiceStates();
	}
	syncLocalVoiceStateWithServer(partial?: {
		self_video?: boolean;
		self_stream?: boolean;
		self_mute?: boolean;
		self_deaf?: boolean;
		viewer_stream_keys?: Array<string>;
	}): void {
		LocalVoiceState.ensurePermissionMute();
		const {guildId, channelId, connectionId} = VoiceConnectionManager.connectionState;
		if (!channelId || !connectionId) return;
		const devicePermission = VoiceDevicePermissionState.getState().permissionStatus;
		const micGranted = MediaPermission.isMicrophoneGranted() || devicePermission === 'granted';
		const payload: VoiceStateSyncPayload = {
			guild_id: guildId,
			channel_id: channelId,
			connection_id: connectionId,
			self_mute:
				micGranted && partial?.self_mute !== undefined
					? partial.self_mute
					: micGranted
						? LocalVoiceState.getSelfMute()
						: true,
			self_deaf: partial?.self_deaf ?? LocalVoiceState.getSelfDeaf(),
			self_video: partial?.self_video ?? LocalVoiceState.getSelfVideo(),
			self_stream: partial?.self_stream ?? LocalVoiceState.getSelfStream(),
			viewer_stream_keys: partial?.viewer_stream_keys ?? LocalVoiceState.getViewerStreamKeys(),
		};
		if (!micGranted && !LocalVoiceState.getSelfMute()) {
			LocalVoiceState.updateSelfMute(true);
		}
		this.voiceStateSync.requestState(payload);
	}
	private async reconcileLocalAudioState(reason: string): Promise<void> {
		const {channelId, connectionId} = VoiceConnectionManager.connectionState;
		if (!channelId || !connectionId) {
			logger.debug('Skipping local audio reconciliation without an active voice connection', {
				reason,
				channelId,
				connectionId,
			});
			return;
		}
		const serverVoiceState = VoiceStateManager.getVoiceStateByConnectionId(connectionId);
		await VoiceMediaManager.reconcileEffectiveAudioState(VoiceConnectionManager.room, {
			channelId,
			serverMute: serverVoiceState?.mute ?? false,
			serverDeaf: serverVoiceState?.deaf ?? false,
		});
		this.syncLocalVoiceStateWithServer();
	}
	getParticipantByUserIdAndConnectionId(
		userId: string,
		connectionId: string | null,
	): LivekitParticipantSnapshot | undefined {
		return VoiceParticipantManager.getParticipantByUserIdAndConnectionId(userId, connectionId);
	}
	upsertParticipant(participant: Participant): void {
		VoiceParticipantManager.upsertParticipant(participant);
	}
	async setCameraEnabled(
		enabled: boolean,
		options?: {
			deviceId?: string;
			sendUpdate?: boolean;
		},
	): Promise<void> {
		await VoiceMediaManager.setCameraEnabled(enabled, options);
	}
	async setScreenShareEnabled(
		enabled: boolean,
		options?: ScreenShareCaptureOptions & {
			sendUpdate?: boolean;
			playSound?: boolean;
			restartIfEnabled?: boolean;
		},
		publishOptions?: TrackPublishOptions,
	): Promise<void> {
		await VoiceMediaManager.setScreenShareEnabled(enabled, options, publishOptions);
	}
	async startDeviceScreenShare(
		options?: DeviceScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<void> {
		await VoiceMediaManager.startDeviceScreenShare(options, publishOptions);
	}
	async replaceActiveDisplayScreenShare(
		options?: ScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		return VoiceMediaManager.replaceActiveDisplayScreenShare(options, publishOptions);
	}
	async replaceActiveDeviceScreenShare(
		options?: DeviceScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		return VoiceMediaManager.replaceActiveDeviceScreenShare(options, publishOptions);
	}
	async ensureLinuxScreenShareAudioPublication(): Promise<boolean> {
		return VoiceMediaManager.ensureLinuxScreenShareAudioPublication();
	}
	async updateActiveScreenShareSettings(
		options?: ScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		return VoiceMediaManager.updateActiveScreenShareSettings(options, publishOptions);
	}
	applyLocalAudioPreferencesForUser(userId: string): void {
		VoiceMediaManager.applyLocalAudioPreferencesForUser(userId, this.room);
	}
	applyAllLocalAudioPreferences(): void {
		VoiceMediaManager.applyAllLocalAudioPreferences(this.room);
	}
	applyLocalInputVolume(): void {
		VoiceMediaManager.applyLocalInputVolume(this.room);
	}
	setLocalVideoDisabled(identity: string, disabled: boolean): void {
		VoiceMediaManager.setLocalVideoDisabled(identity, disabled, this.room, this.connectionId);
	}
	applyPushToTalkHold(held: boolean): void {
		VoiceMediaManager.applyPushToTalkHold(held, this.room, () => this.getCurrentUserVoiceState());
	}
	applyPushToMuteHold(held: boolean): void {
		VoiceMediaManager.applyPushToMuteHold(held, this.room, () => this.getCurrentUserVoiceState());
	}
	handlePushToTalkModeChange(): void {
		VoiceMediaManager.handlePushToTalkModeChange(this.room, () => this.getCurrentUserVoiceState());
	}
	getMuteReason(voiceState: VoiceState | null): 'guild' | 'voice_push_to_talk' | 'self' | null {
		return VoiceMediaManager.getMuteReason(voiceState);
	}
	async toggleCameraFromKeybind(): Promise<void> {
		await VoiceMediaManager.toggleCameraFromKeybind();
	}
	async toggleScreenShareFromKeybind(): Promise<void> {
		await VoiceMediaManager.toggleScreenShareFromKeybind();
	}
	private startTracking(roomOverride?: Room | null): void {
		const room = roomOverride ?? VoiceConnectionManager.room;
		if (!room) {
			logger.warn('No room available');
			return;
		}
		this.statsManager.setRoom(room);
		this.statsManager.startLatencyTracking();
		this.statsManager.startStatsTracking();
		VoiceSubscriptionManager.setRoom(room);
		VoicePermissionManager.initializeSubscriptions(room);
		this.outputDeviceSyncDisposer?.();
		this.outputDeviceSyncDisposer = bindOutputDeviceSync(room);
		this.audioPreferencesSyncDisposer?.();
		this.audioPreferencesSyncDisposer = this.bindAudioPreferencesSync(room);
		this.startAfkTracking();
		logger.info('All tracking started');
	}
	private stopTracking(): void {
		this.statsManager.stopLatencyTracking();
		this.statsManager.stopStatsTracking();
		VoiceSubscriptionManager.cleanup();
		this.outputDeviceSyncDisposer?.();
		this.outputDeviceSyncDisposer = null;
		this.audioPreferencesSyncDisposer?.();
		this.audioPreferencesSyncDisposer = null;
		this.stopAfkTracking();
		logger.info('All tracking stopped');
	}
	private bindAudioPreferencesSync(room: Room): () => void {
		return reaction(
			() => ({
				inputVolume: VoiceSettings.inputVolume,
				outputVolume: VoiceSettings.outputVolume,
				participantVolumes: ParticipantVolume.volumes,
				participantMutes: ParticipantVolume.localMutes,
				connectionVolumes: ParticipantVolume.connectionVolumesByLocalConnectionId,
				streamAudioEntries: StreamAudioPrefs.entries,
			}),
			(current, previous) => {
				if (current.inputVolume !== previous.inputVolume) {
					VoiceMediaManager.applyLocalInputVolume(room);
				}
				if (
					current.outputVolume !== previous.outputVolume ||
					current.participantVolumes !== previous.participantVolumes ||
					current.participantMutes !== previous.participantMutes ||
					current.connectionVolumes !== previous.connectionVolumes ||
					current.streamAudioEntries !== previous.streamAudioEntries
				) {
					VoiceMediaManager.applyAllLocalAudioPreferences(room);
				}
			},
		);
	}
	private startAfkTracking(): void {
		this.stopAfkTracking();
		this.afkIntervalId = setInterval(() => {
			if (!VoiceConnectionManager.connected || !VoiceConnectionManager.guildId || !VoiceConnectionManager.channelId)
				return;
			if (!Idle.isIdle()) return;
			const idleSince = Idle.getIdleSince();
			if (!idleSince) return;
			const guild = Guilds.getGuild(VoiceConnectionManager.guildId);
			if (!guild?.afkChannelId || !guild.afkTimeout) return;
			if (VoiceConnectionManager.channelId === guild.afkChannelId) return;
			if (Math.floor((Date.now() - idleSince) / 1000) >= guild.afkTimeout) {
				void this.moveToAfkChannel();
			}
		}, AFK_CHECK_INTERVAL_MS);
	}
	private stopAfkTracking(): void {
		if (this.afkIntervalId !== null) {
			clearInterval(this.afkIntervalId);
			this.afkIntervalId = null;
		}
	}
	private playSpectatorSounds(oldStreamKeys: Array<string>, newStreamKeys: Array<string>): void {
		const myConnectionId = VoiceConnectionManager.connectionId;
		if (!myConnectionId) return;
		const myViewerStreamKeys = LocalVoiceState.getViewerStreamKeys();
		const isRelevantStream = (key: string): boolean => {
			const parsed = parseStreamKey(key);
			if (!parsed) return false;
			if (parsed.connectionId === myConnectionId) return true;
			if (myViewerStreamKeys.includes(key)) return true;
			return false;
		};
		const oldRelevant = new Set(oldStreamKeys.filter(isRelevantStream));
		const newRelevant = new Set(newStreamKeys.filter(isRelevantStream));
		const joined = newStreamKeys.filter((k) => isRelevantStream(k) && !oldRelevant.has(k));
		const left = oldStreamKeys.filter((k) => isRelevantStream(k) && !newRelevant.has(k));
		if (joined.length > 0) {
			SoundCommands.playSound(SoundType.ViewerJoin);
		} else if (left.length > 0) {
			SoundCommands.playSound(SoundType.ViewerLeave);
		}
	}
	disconnectRemoteDevice(guildId: string, connectionId: string): void {
		sendVoiceStateDisconnect(guildId, connectionId);
	}
	disconnectAllRemoteDevices(
		devices: ReadonlyArray<{
			guildId: string;
			connectionId: string;
		}>,
	): void {
		for (const device of devices) {
			sendVoiceStateDisconnect(device.guildId, device.connectionId);
		}
	}
	async moveToAfkChannel(): Promise<void> {
		const {guildId, channelId, connected} = VoiceConnectionManager.connectionState;
		if (!connected || !guildId || !channelId) return;
		const guild = Guilds.getGuild(guildId);
		if (!guild?.afkChannelId || channelId === guild.afkChannelId) return;
		SoundCommands.playSound(SoundType.UserMove);
		await this.connectToVoiceChannel(guildId, guild.afkChannelId);
	}
	getLastConnectedChannel(): {
		guildId: string;
		channelId: string;
	} | null {
		return VoiceConnectionManager.lastConnectedChannel;
	}
	getShouldReconnect(): boolean {
		return VoiceConnectionManager.shouldAutoReconnect;
	}
	markReconnectionAttempted(): void {
		VoiceConnectionManager.markReconnectionAttempted();
	}
	async handleLogout(): Promise<void> {
		this.cancelPendingServerDisconnect();
		this.clearViewerStreamKeys();
		this.stopTracking();
		this.activeConnection = null;
		this.pendingUserMove = null;
		this.pendingSessionRestore = null;
		VoiceSessionRestore.clearSnapshot();
		VoiceConnectionManager.cleanup();
		VoiceStateManager.clearAllVoiceStates();
		VoiceParticipantManager.clear();
		VoicePermissionManager.reset();
		VoiceMediaManager.resetStreamTracking();
		VoiceMediaStateCoordinator.resetLocalMediaState('logout');
		this.voiceStateSync.reset();
		try {
			await voiceStatsDB.clear();
		} catch (error) {
			logger.error('Failed to clear voice stats DB during logout', error);
			throw error;
		} finally {
			logger.info('Cleanup complete');
		}
	}
	handleGatewayError(error: GatewayErrorData): void {
		const voiceErrorCodes = new Set<GatewayErrorCode>([
			GatewayErrorCodes.VOICE_CONNECTION_NOT_FOUND,
			GatewayErrorCodes.VOICE_CHANNEL_NOT_FOUND,
			GatewayErrorCodes.VOICE_INVALID_CHANNEL_TYPE,
			GatewayErrorCodes.VOICE_MEMBER_NOT_FOUND,
			GatewayErrorCodes.VOICE_MEMBER_TIMED_OUT,
			GatewayErrorCodes.VOICE_USER_NOT_IN_VOICE,
			GatewayErrorCodes.VOICE_GUILD_NOT_FOUND,
			GatewayErrorCodes.VOICE_PERMISSION_DENIED,
			GatewayErrorCodes.VOICE_CHANNEL_FULL,
			GatewayErrorCodes.VOICE_MISSING_CONNECTION_ID,
			GatewayErrorCodes.VOICE_TOKEN_FAILED,
			GatewayErrorCodes.VOICE_UNCLAIMED_ACCOUNT,
		]);
		if (!voiceErrorCodes.has(error.code)) {
			return;
		}
		logger.warn(`Voice-related gateway error: [${error.code}] ${error.message}`);
		if (error.code === GatewayErrorCodes.VOICE_CONNECTION_NOT_FOUND) {
			if (VoiceConnectionManager.connecting) {
				logger.info('Connection not found while connecting, aborting');
				this.pendingSessionRestore = null;
				VoiceConnectionManager.abortConnection();
			}
		} else if (
			error.code === GatewayErrorCodes.VOICE_PERMISSION_DENIED ||
			error.code === GatewayErrorCodes.VOICE_CHANNEL_FULL ||
			error.code === GatewayErrorCodes.VOICE_MEMBER_TIMED_OUT ||
			error.code === GatewayErrorCodes.VOICE_UNCLAIMED_ACCOUNT
		) {
			if (VoiceConnectionManager.connecting && !VoiceConnectionManager.connected) {
				logger.info('Permission denied, channel full, or timeout while connecting, aborting');
				this.pendingSessionRestore = null;
				VoiceConnectionManager.abortConnection();
			}
			if (error.code === GatewayErrorCodes.VOICE_MEMBER_TIMED_OUT) {
				if (!this.i18n) {
					throw new Error('MediaEngineFacade: i18n not initialized');
				}
				ToastCommands.createToast({
					type: 'error',
					children: this.i18n._(msg`You can't join while you're on timeout.`),
				});
			} else if (error.code === GatewayErrorCodes.VOICE_UNCLAIMED_ACCOUNT) {
				if (!this.i18n) {
					throw new Error('MediaEngineFacade: i18n not initialized');
				}
				ToastCommands.createToast({
					type: 'error',
					children: this.i18n._(msg`Claim your account to join this voice channel.`),
				});
			}
		} else if (error.code === GatewayErrorCodes.VOICE_TOKEN_FAILED) {
			if (VoiceConnectionManager.connecting) {
				logger.info('Token failed while connecting, aborting');
				this.pendingSessionRestore = null;
				VoiceConnectionManager.abortConnection();
			}
		}
	}
	cleanup(): void {
		this.cancelPendingServerDisconnect();
		this.clearViewerStreamKeys();
		this.stopTracking();
		this.activeConnection = null;
		this.pendingUserMove = null;
		this.pendingSessionRestore = null;
		this.statsManager.cleanup();
		VoiceSubscriptionManager.cleanup();
		VoicePermissionManager.reset();
		VoiceMediaManager.resetStreamTracking();
		VoiceConnectionManager.cleanup();
		VoiceStateManager.clearAllVoiceStates();
		VoiceParticipantManager.clear();
		VoiceMediaStateCoordinator.resetLocalMediaState('cleanup');
		this.voiceStateSync.reset();
		this.detachDiagnosticsFromRoom();
		this.setDiagState('idle', 'cleanup');
	}
	reset(): void {
		this.cancelPendingServerDisconnect();
		this.clearViewerStreamKeys();
		this.statsManager.reset();
		this.activeConnection = null;
		this.pendingUserMove = null;
		this.pendingSessionRestore = null;
		VoiceConnectionManager.resetConnectionState();
		VoiceConnectionManager.resetReconnectState();
		VoicePermissionManager.reset();
		VoiceMediaManager.resetStreamTracking();
		VoiceParticipantManager.clear();
		VoiceMediaStateCoordinator.resetLocalMediaState('cleanup');
		this.voiceStateSync.reset();
	}
	private async restoreLocalMediaState(): Promise<void> {
		const room = VoiceConnectionManager.room;
		const participant = room?.localParticipant ?? null;
		if (!participant) return;
		if (LocalVoiceState.getSelfVideo() && !participant.isCameraEnabled) {
			await VoiceMediaManager.setCameraEnabled(true, {
				deviceId: VoiceSettings.getVideoDeviceId(),
				sendUpdate: false,
			});
		}
		if (LocalVoiceState.getSelfStream() && !participant.isScreenShareEnabled) {
			try {
				await VoiceMediaManager.setScreenShareEnabled(true, {sendUpdate: false});
			} catch (error) {
				logger.warn('Failed to restore screen share', {error});
				VoiceMediaStateCoordinator.applyScreenShareState(false, {reason: 'server', sendUpdate: false});
			}
		}
	}
	private async restorePendingSessionMedia(): Promise<void> {
		const pendingRestore = this.pendingSessionRestore;
		this.pendingSessionRestore = null;
		if (!pendingRestore) {
			return;
		}
		if (pendingRestore.restoreVideo) {
			try {
				await VoiceMediaManager.setCameraEnabled(true, {
					deviceId: VoiceSettings.getVideoDeviceId(),
					sendUpdate: false,
				});
			} catch (error) {
				logger.warn('Failed to restore camera from startup voice restore', {error});
			}
		}
		if (!pendingRestore.restoreStream) {
			return;
		}
		try {
			await VoiceMediaManager.setScreenShareEnabled(true, {sendUpdate: false});
		} catch (error) {
			logger.warn('Failed to restore screen share from startup voice restore', {error});
			VoiceMediaStateCoordinator.applyScreenShareState(false, {reason: 'server', sendUpdate: false});
		}
		if (!LocalVoiceState.getSelfStream() && this.i18n) {
			ToastCommands.createToast({
				type: 'info',
				children: this.i18n._(
					msg`Reconnect succeeded. Pick a screen again if you want to resume your previous stream.`,
				),
			});
		}
	}
	private navigateToVoiceChannel(guildId: string | null, channelId: string): void {
		const targetGuildId = guildId ?? ME;
		NavigationCommands.selectChannel(targetGuildId, channelId);
	}
	private setDiagState(next: DiagState, reason: string, extra?: Record<string, unknown>): void {
		const previous = this.diagState;
		if (previous === next) {
			diagnostics.debug('state.transition.noop', {state: next, reason, ...extra});
			return;
		}
		this.diagState = next;
		diagnostics.info('state.transition', {
			from: previous,
			to: next,
			reason,
			guildId: VoiceConnectionManager.guildId,
			channelId: VoiceConnectionManager.channelId,
			connectionId: VoiceConnectionManager.connectionId,
			voiceServerEndpointPresent: Boolean(VoiceConnectionManager.voiceServerEndpoint),
			participantCount: Object.keys(VoiceParticipantManager.participants).length,
			voiceStats: this.statsManager.voiceStats,
			...extra,
		});
	}
	private async logSessionStart(guildId: string | null, channelId: string): Promise<void> {
		const inputDeviceIdHash = await hashDeviceIdForInstrumentation(VoiceSettings.getInputDeviceId());
		const outputDeviceIdHash = await hashDeviceIdForInstrumentation(VoiceSettings.getOutputDeviceId());
		const videoDeviceIdHash = await hashDeviceIdForInstrumentation(VoiceSettings.getVideoDeviceId());
		let displayShareEnvironment: unknown = null;
		try {
			const mod = await import('@app/features/voice/utils/ScreenShareEnvironment');
			displayShareEnvironment = await mod.getDisplayShareEnvironment();
		} catch (error) {
			displayShareEnvironment = {error: summarizeForInstrumentation(error)};
		}
		let permissions: Record<string, string> = {};
		try {
			const queryPermission = async (name: string): Promise<string> => {
				const perms = navigator.permissions as
					| {
							query?: (descriptor: {name: string}) => Promise<PermissionStatus>;
					  }
					| undefined;
				if (!perms?.query) return 'unsupported';
				try {
					return (await perms.query({name})).state;
				} catch (error) {
					return `error:${(error as Error).message ?? 'unknown'}`;
				}
			};
			permissions = {
				microphone: await queryPermission('microphone'),
				camera: await queryPermission('camera'),
				displayCapture: await queryPermission('display-capture'),
				speakerSelection: await queryPermission('speaker-selection'),
			};
		} catch {
			permissions = {error: 'permission_query_failed'};
		}
		diagnostics.info('session.start', {
			guildId,
			channelId,
			voiceServerEndpoint: VoiceConnectionManager.voiceServerEndpoint,
			deviceIdHashes: {
				input: inputDeviceIdHash,
				output: outputDeviceIdHash,
				video: videoDeviceIdHash,
			},
			codecs: {
				preferredVideoCodec: VoiceSettings.getPreferredVideoCodec(),
				preferredScreenShareCodec: VoiceSettings.getPreferredScreenShareCodec(),
			},
			displayShareEnvironment,
			permissions,
			audio: {
				echoCancellation: VoiceSettings.getEchoCancellation(),
				noiseSuppression: VoiceSettings.getNoiseSuppression(),
				autoGainControl: VoiceSettings.getAutoGainControl(),
				deepFilterNoiseSuppression: VoiceSettings.getDeepFilterNoiseSuppression(),
				voiceProcessingMode: VoiceSettings.getVoiceProcessingMode(),
			},
		});
	}
	private attachDiagnosticsToRoom(room: Room | null, attemptId: number): void {
		this.detachDiagnosticsFromRoom();
		if (!room) return;
		const lkRoom = room as unknown as LivekitRoomLike;
		this.diagAttemptId = attemptId;
		const wrap = (eventName: string) => {
			const listener = (...args: Array<unknown>): void => {
				if (this.diagAttemptId !== attemptId) return;
				try {
					diagnostics.info(`room.${eventName}`, {
						attemptId,
						args: args.map((a) => summarizeLivekitArg(a)),
						roomState: lkRoom.state ?? null,
					});
				} catch {}
			};
			try {
				lkRoom.on(eventName, listener);
				this.diagRoomDisposers.push(() => {
					try {
						lkRoom.off?.(eventName, listener);
					} catch {}
				});
			} catch {}
		};
		for (const event of LIVEKIT_ROOM_EVENTS) {
			wrap(event);
		}
		const local = lkRoom.localParticipant;
		if (local && typeof local.on === 'function') {
			const localEvents = [
				'localTrackPublished',
				'localTrackUnpublished',
				'mediaDevicesError',
				'participantPermissionsChanged',
				'trackMuted',
				'trackUnmuted',
				'trackStreamStateChanged',
			];
			for (const event of localEvents) {
				const listener = (...args: Array<unknown>): void => {
					if (this.diagAttemptId !== attemptId) return;
					try {
						diagnostics.info(`localParticipant.${event}`, {
							attemptId,
							identity: local.identity ?? null,
							sid: local.sid ?? null,
							args: args.map((a) => summarizeLivekitArg(a)),
						});
					} catch {}
				};
				try {
					local.on(event, listener);
					this.diagRoomDisposers.push(() => {
						try {
							local.off?.(event, listener);
						} catch {}
					});
				} catch {}
			}
		}
		this.startDiagnosticsStatsSampler(lkRoom, attemptId);
	}
	private detachDiagnosticsFromRoom(): void {
		this.diagAttemptId = null;
		for (const dispose of this.diagRoomDisposers) {
			try {
				dispose();
			} catch {}
		}
		this.diagRoomDisposers = [];
		this.stopDiagnosticsStatsSampler();
	}
	private startDiagnosticsStatsSampler(lkRoom: LivekitRoomLike, attemptId: number): void {
		this.stopDiagnosticsStatsSampler();
		const sample = async (): Promise<void> => {
			if (this.diagAttemptId !== attemptId) return;
			try {
				const pub = lkRoom.engine?.pcManager?.publisher?.getStats?.();
				const sub = lkRoom.engine?.pcManager?.subscriber?.getStats?.();
				const [pubReport, subReport] = await Promise.all([
					pub ? pub.catch(() => undefined) : Promise.resolve(undefined),
					sub ? sub.catch(() => undefined) : Promise.resolve(undefined),
				]);
				diagnostics.info('webrtc.stats.sample', {
					attemptId,
					publisher: statsReportToArray(pubReport),
					subscriber: statsReportToArray(subReport),
					voiceStats: this.statsManager.voiceStats,
				});
			} catch (error) {
				diagnostics.warn('webrtc.stats.sample.error', {
					attemptId,
					error: summarizeForInstrumentation(error),
				});
			}
		};
		void sample();
		this.diagStatsTimer = setInterval(() => {
			void sample();
		}, WEBRTC_STATS_INTERVAL_MS);
	}
	private stopDiagnosticsStatsSampler(): void {
		if (this.diagStatsTimer != null) {
			clearInterval(this.diagStatsTimer);
			this.diagStatsTimer = null;
		}
	}
	private clearViewerStreamKeys(): void {
		if (LocalVoiceState.getViewerStreamKeys().length === 0) {
			return;
		}
		LocalVoiceState.updateViewerStreamKeys([]);
	}
}
const instance = new MediaEngineFacade();
(
	window as typeof window & {
		_mediaEngineFacade?: MediaEngineFacade;
	}
)._mediaEngineFacade = instance;
export default instance;
